import React, { useEffect, useState, useRef } from "react";
import AdminSidebar from "./AdminSidebar";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtri
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterIsAdmin, setFilterIsAdmin] = useState("all"); // all, yes, no
  const [filterBlocked, setFilterBlocked] = useState("all"); // all, yes, no

  // Menu contestuale
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    user: null,
  });
  const contextMenuRef = useRef(null);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("jwt_token");
        if (!token) throw new Error("Token non trovato");

        const host = import.meta.env.VITE_HOST;
        const port = import.meta.env.VITE_PORT;

        const res = await fetch(`${host}:${port}/api/v1/users/admin/get_list_user`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const data = await res.json();

        if (res.ok && data.status === "OK") {
          setUsers(data.data);
        } else {
          throw new Error(data.message || "Errore nel recupero utenti");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Chiudo menu se clicco fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setContextMenu((c) => ({ ...c, visible: false }));
      }
    };
    if (contextMenu.visible) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [contextMenu.visible]);

  // Filtro utenti
  const filteredUsers = users.filter((u) => {
    const nameMatch =
      filterName === "" ||
      u.name.toLowerCase().includes(filterName.toLowerCase()) ||
      u.surname.toLowerCase().includes(filterName.toLowerCase());
    if (!nameMatch) return false;

    if (filterEmail && !u.email.toLowerCase().includes(filterEmail.toLowerCase()))
      return false;

    if (filterIsAdmin !== "all") {
      if (filterIsAdmin === "yes" && !u.is_admin) return false;
      if (filterIsAdmin === "no" && u.is_admin) return false;
    }

    if (filterBlocked !== "all") {
      if (filterBlocked === "yes" && !u.blocked) return false;
      if (filterBlocked === "no" && u.blocked) return false;
    }

    return true;
  });

  // Gestione tasto destro riga
  const handleContextMenu = (event, user) => {
    event.preventDefault();

    const clickX = event.clientX;
    const clickY = event.clientY;
    const screenW = window.innerWidth;
    const screenH = window.innerHeight;

    const menuWidth = 200; // larghezza menu contestuale stimata
    const menuHeight = 170; // altezza menu stimata

    let x = clickX;
    let y = clickY;

    // Se va fuori viewport a destra, sposto a sinistra
    if (clickX + menuWidth > screenW) {
      x = screenW - menuWidth - 10;
    }
    // Se va fuori viewport in basso, sposto su
    if (clickY + menuHeight > screenH) {
      y = screenH - menuHeight - 10;
    }

    setContextMenu({
      visible: true,
      x,
      y,
      user,
    });
  };

  const handleAction = (action) => {
    if (!contextMenu.user) return;
    console.log(`Azione: ${action} su utente`, contextMenu.user);
    setContextMenu((c) => ({ ...c, visible: false }));
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-64 text-gray-600">
        Caricamento utenti...
      </div>
    );

  if (error)
    return (
      <div className="text-red-600 text-center mt-10 font-semibold">
        Errore: {error}
      </div>
    );

  return (
    <div className="flex min-h-screen bg-white">
      <AdminSidebar className="bg-white shadow-md p-5 w-64" />

      <main className="flex-1 p-8 relative">
        <h2 className="text-xl font-semibold mb-6" style={{ color: "#94c41c" }}>
          Lista Utenti
        </h2>

        {/* FILTRI */}
        <div className="mb-6 flex flex-wrap gap-4 text-black items-center">
          <input
            type="text"
            placeholder="Filtra per nome o cognome"
            className="border px-3 py-2 rounded min-w-[250px]"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
          <input
            type="text"
            placeholder="Filtra per email"
            className="border px-3 py-2 rounded"
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
          />
          <select
            className="border px-3 py-2 rounded"
            value={filterIsAdmin}
            onChange={(e) => setFilterIsAdmin(e.target.value)}
          >
            <option value="all">Tutti</option>
            <option value="yes">Admin</option>
            <option value="no">Non Admin</option>
          </select>
          <select
            className="border px-3 py-2 rounded"
            value={filterBlocked}
            onChange={(e) => setFilterBlocked(e.target.value)}
          >
            <option value="all">Tutti</option>
            <option value="yes">Bloccati</option>
            <option value="no">Non Bloccati</option>
          </select>
        </div>

        {filteredUsers.length === 0 ? (
          <p className="text-gray-600">Nessun utente trovato.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-300 shadow bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cognome
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email Verificata
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bloccato
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Domini Acquistati
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr
                    key={user.user_id}
                    className="hover:bg-gray-50 cursor-context-menu"
                    onContextMenu={(e) => handleContextMenu(e, user)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {user.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {user.surname}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td
                      className={`px-4 py-3 whitespace-nowrap text-sm font-semibold ${
                        user.email_verified ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {user.email_verified ? "Sì" : "No"}
                    </td>
                    <td
                      className={`px-4 py-3 whitespace-nowrap text-sm font-semibold ${
                        user.is_admin ? "text-blue-600" : "text-gray-500"
                      }`}
                    >
                      {user.is_admin ? "Sì" : "No"}
                    </td>
                    <td
                      className={`px-4 py-3 whitespace-nowrap text-sm font-semibold ${
                        user.blocked ? "text-red-600" : "text-green-600"
                      }`}
                    >
                      {user.blocked ? "Sì" : "No"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {user.domains ? user.domains.length : 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {contextMenu.visible && (
          <ul
            ref={contextMenuRef}
            className="fixed bg-white border border-gray-300 rounded shadow-lg w-48 z-50"
            style={{
              top: contextMenu.y,
              left: contextMenu.x,
              userSelect: "none",
            }}
          >
            <li
              className="px-4 py-2 hover:bg-gray-100 text-black cursor-pointer"
              onClick={() => handleAction("Blocca")}
            >
              {contextMenu.user.blocked ? "Sblocca" : "Blocca"}
            </li>
            <li
              className="px-4 py-2 hover:bg-gray-100 text-black cursor-pointer"
              onClick={() =>
                handleAction(contextMenu.user.is_admin ? "Rimuovi admin" : "Rendi admin")
              }
            >
              {contextMenu.user.is_admin ? "Rimuovi admin" : "Rendi admin"}
            </li>
            <li
              className="px-4 py-2 hover:bg-gray-100 text-black cursor-pointer"
              onClick={() => handleAction("Elimina utente")}
            >
              Elimina utente
            </li>
            {!contextMenu.user.email_verified && (
              <li
                className="px-4 py-2 hover:bg-gray-100 text-black cursor-pointer"
                onClick={() => handleAction("Conferma email manualmente")}
              >
                Conferma email manualmente
              </li>
            )}
          </ul>
        )}
      </main>
    </div>
  );
};

export default UserList;
